import Seo from "@/components/Seo";
import { localBusinessSchema, breadcrumbSchema } from "@/data/site";
import { PageHero, Section, SectionHead, CtaBand } from "@/components/sections";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion";
import { CalendarCheck, Users, Timer } from "lucide-react";

const steps = [
  { n: "01", title: "Brief & plan", text: "We agree scope, price and content together. A small booking reserves your sprint in our schedule, so your project has a real start and end date." },
  { n: "02", title: "We build (weeks 1–2)", text: "We build your first working version and put it online on a private link. You see real progress quickly, not just mockups." },
  { n: "03", title: "You review once", text: "You send all your feedback together, within two weeks, through our simple review form. We make the agreed changes in one focused round." },
  { n: "04", title: "Launch (weeks 3–4)", text: "You approve, we connect your domain, go live, and train your team so you're confident running your new site." },
];

const yourPart = [
  { icon: CalendarCheck, title: "Send your content on time", text: "Logo, photos, text and details by the agreed date. The build clock starts when your content is in." },
  { icon: Users, title: "One decision-maker", text: "Name one person to give feedback and approve. It prevents mixed messages and delays." },
  { icon: Timer, title: "Review within two weeks", text: "Send all your feedback together, in one round, through our simple review form." },
];

const milestones = [
  { pct: "20%", label: "to book" },
  { pct: "30%", label: "when scope is agreed" },
  { pct: "30%", label: "when your draft is ready" },
  { pct: "20%", label: "before we go live" },
];

export default function Process() {
  return (
    <>
      <Seo
        title="How We Work — A Clear Four-Week Web Design Process in Ghana | Sankofa Launch Studio"
        description="Our structured four-week process takes your website from idea to launch: brief & plan, we build, you review once, and launch. Fair milestone payments of 20/30/30/20."
        jsonLd={[
          localBusinessSchema(),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "How We Work", path: "/process" }]),
        ]}
      />
      <PageHero
        eyebrow="How We Work"
        breadcrumb="Home / How We Work"
        title="A clear four-week process from idea to launch"
        intro="We deliver most websites in four weeks using a clear, structured sprint. Here's exactly how each week works — no surprises, no endless changes."
      />

      <Section className="bg-cream">
        <div className="relative mx-auto max-w-3xl">
          <div className="absolute left-6 top-4 bottom-4 w-px bg-navy/15" />
          <div className="space-y-8">
            {steps.map((s) => (
              <Reveal key={s.n}>
                <div className="relative flex gap-6" data-testid={`process-step-${s.n}`}>
                  <div className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold font-serif text-lg font-700 text-navy-dark">
                    {s.n}
                  </div>
                  <div className="rounded-2xl bg-white p-7 shadow-soft">
                    <h3 className="font-serif text-2xl font-600 text-navy">{s.title}</h3>
                    <p className="mt-3 leading-relaxed text-ink">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <SectionHead eyebrow="Your part keeps it fast" title="A great website is a partnership." />
          <StaggerGroup className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {yourPart.map((y) => (
              <StaggerItem key={y.title}>
                <article className="h-full rounded-2xl bg-cream p-8 shadow-soft" data-testid={`yourpart-${y.title.split(" ")[0].toLowerCase()}`}>
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-navy text-white">
                    <y.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-6 font-serif text-xl font-600 text-navy">{y.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink">{y.text}</p>
                </article>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <Section className="bg-cream">
        <Reveal>
          <div className="rounded-2xl bg-navy p-8 md:p-12" data-testid="payments-recap">
            <h2 className="font-serif text-2xl font-600 text-white md:text-3xl">Fair and simple payments</h2>
            <p className="mt-3 text-white/75">You never pay everything up front. You pay in stages as the project moves forward.</p>
            <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
              {milestones.map((m, i) => (
                <div key={i} className="rounded-xl bg-white/[0.06] p-6 text-center">
                  <div className="font-serif text-3xl font-700 text-gold">{m.pct}</div>
                  <div className="mt-2 text-sm text-white/70">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </Section>

      <CtaBand title="Start your project" text="Book your briefing this week and we'll get you into the next sprint." />
    </>
  );
}
